import React from 'react';
import { format } from 'date-fns';
import TextField, {
    TextFieldProps,
} from 'components/interface/textField/TextField';
import { extractNumber } from 'utils/utils';
import { PopperProps } from '@mui/material';
import { IconButton } from 'components/IconButton';
import { Calendar } from 'components/DateRangePicker/Calendar';
import { parseCalendarDate } from 'components/DateRangePicker/dateOnly';
import { CalendarPopover } from 'components/DateRangePicker/CalendarPopover';

// Retain the bounds of the previous MUI picker.
const defaultMinDate = '1900-01-01';
const defaultMaxDate = '2099-12-31';

interface DatePickerProps
    extends Omit<TextFieldProps, 'value' | 'onChange' | 'onError'> {
    // Calendar dates use yyyy-MM-dd strings without a time or timezone.
    value?: string | null;
    // Will override the date (value) if it is later than the current value
    // Used in start-end date scenarios, so that when the start date is set, the end date is set to the same date so that the user do not have to manually scroll to the same date.
    minDate?: string | null;
    // Only triggered for a valid yyyy-MM-dd date.
    onChange: (date: string) => void;
    onError?: (b: boolean) => void;
    required?: boolean;
    className?: string;

    headless?: boolean; // if no textfield should be rendered.
    open?: boolean; // controls if the picker is open or not, only works with headless
    onClose?: () => void; // only needed in heeadless mode
    PopperProps?: Partial<PopperProps>; // in headless mode you must set the anchorEl
    disablePast?: boolean;
    onClear?: () => void;
}

export const DatePicker = ({
    value,
    minDate: minDateProp,
    onChange,
    onError,
    className: classNameProp,
    disabled,
    headless,
    open: openProp,
    onClose,
    PopperProps,
    required,
    fullWidth,
    disablePast,
    onClear,
    placeholder,
    ...rest
}: DatePickerProps): JSX.Element => {
    const [_text, setText] = React.useState<string>(''); // internal state in string format
    const [open, setOpen] = React.useState<boolean>(false);
    const [error, setError] = React.useState<boolean>(false);
    const [cursor, setCursor] = React.useState(0);
    const [forceUpdate, setForceUpdate] = React.useState(false);
    const firstRender = React.useRef<boolean>(true);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const dateRef = React.useRef<HTMLDivElement | null>(null);

    const text = value && parseCalendarDate(value) ? value : '';

    React.useEffect(() => {
        setText(text);
        setError(false);
    }, [text]);

    const minDate =
        minDateProp && parseCalendarDate(minDateProp) ? minDateProp : null;
    const calendarMinDate = minDate ?? defaultMinDate;
    const today = format(new Date(), 'yyyy-MM-dd');
    const disableBefore =
        disablePast && today > calendarMinDate ? today : calendarMinDate;

    // Updates the displayed value according to the minDate prop.
    React.useEffect(() => {
        if (minDate && text && minDate > text) {
            setText(minDate);
        }
    }, [minDate, text]);

    React.useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        // the reason for this logic being here and not in the update function, is because the setText is not synchronous
        // so if we set the caret position before the text is updated, the text field will overwrite it when the text is actually updated.
        // So this has to happen AFTER the text is updated.
        if (inputRef.current) {
            inputRef.current.selectionStart = cursor;
            inputRef.current.selectionEnd = cursor;
        }
        if (forceUpdate) {
            setForceUpdate(false);
        }
    }, [_text, cursor, forceUpdate]);

    const handleOnchange = (v: string) => {
        if (parseCalendarDate(v)) {
            // if it is the same date as the current value, do not update
            if (v !== text) {
                onChange(v);
            }

            setError(false);
        } else {
            if (v.length > 0) {
                setError(true);
            } else {
                setError(false);
            }
        }
    };

    React.useEffect(() => {
        if (onError) {
            onError(error);
        }
    }, [error, onError]);

    React.useEffect(() => {
        if (headless) {
            setOpen(openProp || false);
        }
    }, [headless, openProp]);

    const handleSetOpen = (value: boolean) => {
        if (onClose && value === false) {
            onClose();
        }
        if (headless) {
            return;
        }
        setOpen(value);
    };

    const onBlur = () => {
        const newText = formatToDate(_text, true);
        setText(newText);
        handleOnchange(newText);
    };

    // Formats the string to the yyyy-mm-dd format if the input is valid, otherwise it returns the string.
    const formatToDate = (s: string, insertZero: boolean) => {
        let v = s.replaceAll('-', '').trim();
        if (v.length === 7 && v[6] !== '0' && insertZero) {
            v = v.slice(0, 6) + 0 + v[6]; //turns 2022012 => 20220102
        }

        if (v.length > 6) {
            return v.slice(0, 4) + '-' + v.slice(4, 6) + '-' + v.slice(6, 8);
        } else if (v.length > 4) {
            return v.slice(0, 4) + '-' + v.slice(4, 6);
        }
        return v;
    };

    const handleChange = (
        v: string,
        _e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (!inputRef.current) {
            return;
        }
        let pos = inputRef.current.selectionStart || 0;

        // If the new position is 5, which is 2022-|01-02
        // Move it to position 6, which is 2022-0|1-02
        // If the new position is 8, which is 2022-01-|02
        // Move it to position 9, which is 2022-01-0|2
        if (pos === 5) {
            pos = 6;
        } else if (pos === 8) {
            pos = 9;
        }

        let newText = v;
        const e = _e.nativeEvent as InputEvent;

        // If there is a simply input of text then replace the character of the current cursor position
        // Only if the date is full, otherwise push the numbers as usual.
        if (e.inputType === 'insertText' && _text.length >= 10) {
            const newInput = extractNumber(e.data || '', 0);
            if (newInput.length > 0) {
                const index = pos - 1;
                newText =
                    _text.slice(0, index) +
                    newInput +
                    _text.slice(index + newInput.length || 0);
            } else {
                // If nothing is written, revert the position of the cursor
                pos -= 1;
            }
        }

        newText = formatToDate(extractNumber(newText, 0), false);
        setText(newText);
        // Existing travel fields update as soon as a complete date is entered.
        handleOnchange(newText);
        setCursor(pos);
        // If the overwritten character is the same as the previous, the text will not update which means that we need to
        // force update the caret position.
        if (newText === _text) {
            setForceUpdate(true);
        }
    };

    return (
        <div
            ref={dateRef}
            className={classNameProp}
            style={fullWidth ? { width: '100%' } : undefined}>
            {headless ? null : (
                <TextField
                    height={40}
                    {...rest}
                    ref={inputRef}
                    value={_text}
                    onChange={handleChange}
                    onBlur={onBlur}
                    error={error || (required && _text.length === 0)}
                    placeholder={placeholder ? placeholder : 'yyyy-mm-dd'}
                    endAdornment={
                        <>
                            {onClear ? (
                                <IconButton
                                    icon='clear'
                                    variant='text'
                                    onClick={onClear}
                                    disabled={disabled || value === null}
                                />
                            ) : null}
                            <IconButton
                                icon='event'
                                variant='text'
                                onClick={() => handleSetOpen(true)}
                                disabled={disabled}
                            />
                        </>
                    }
                    onKeyDown={e => {
                        if (e.key === 'Escape') {
                            handleSetOpen(false);
                        } else if (e.key === 'Enter') {
                            handleSetOpen(true);
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                    helperText={error ? 'Felaktigt format' : ''}
                    disabled={disabled}
                    required={required}
                    fullWidth={fullWidth}
                />
            )}
            <CalendarPopover
                animation='grow'
                zIndex={100000}
                anchorEl={dateRef.current}
                placement='bottom'
                {...PopperProps}
                open={open}
                onClose={() => handleSetOpen(false)}
                onKeyDown={e => {
                    if (e.key === 'Escape') {
                        handleSetOpen(false);
                    }
                }}
                PaperProps={{ elevation: 8, sx: { overflow: 'hidden' } }}
                transition>
                <Calendar
                    key={text || 'empty'}
                    periods={text ? [{ start: text, end: text }] : []}
                    variant='day'
                    onChange={periods => {
                        const selected = periods[0]?.start;
                        if (selected) {
                            setText(selected);
                            handleOnchange(selected);
                        }
                    }}
                    onSelectionComplete={() => handleSetOpen(false)}
                    disableBefore={disableBefore}
                    disableAfter={defaultMaxDate}
                />
            </CalendarPopover>
        </div>
    );
};
